import * as _ from 'lodash';

/**
 * Recursively removes null and undefined values from an object or array.
 *
 * IMPORTANT: This function properly handles primitives (strings, numbers, booleans)
 * to avoid the character-indexed object bug that occurred when strings were
 * passed to Object.entries().
 *
 * @param data - The object, array, or primitive value to process
 * @returns A new object/array with null/undefined values removed, or the primitive value unchanged
 *
 * @example
 * // Object with nested null values
 * dropNullUndefined({ a: 1, b: null, c: { d: 2, e: undefined } })
 * // Returns: { a: 1, c: { d: 2 } }
 *
 * @example
 * // Array with objects
 * dropNullUndefined([{ a: 1, b: null }, { c: 2 }])
 * // Returns: [{ a: 1 }, { c: 2 }]
 *
 * @example
 * // Array of strings (properly preserved)
 * dropNullUndefined({ reasons: ["script renewal", "follow-up"] })
 * // Returns: { reasons: ["script renewal", "follow-up"] }
 *
 */
export function dropNullUndefined(data: any): any {
  // Handle primitives: strings, numbers, booleans, null, undefined
  // This prevents the bug where Object.entries(string) creates character-indexed objects
  if (typeof data !== 'object' || data === null) {
    return data;
  }

  // Handle arrays: filter nulls and recurse only on plain objects
  if (Array.isArray(data)) {
    return data
      .filter(item => item != null)
      .map(item => _.isPlainObject(item) ? dropNullUndefined(item) : item);
  }

  // Handle plain objects: remove null/undefined values and recurse on nested objects/arrays
  return Object.entries(data).reduce((acc, [key, value]) => {
    if (value == null) {
      return acc;
    }
    return {
      ...acc,
      [key]: _.isPlainObject(value) || Array.isArray(value) ? dropNullUndefined(value) : value,
    };
  }, {});
}
